import {
    ClientOrganization,
    getClientOrganizationsService,
} from "@/service/client.service";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "@store";
import { persistReducer } from "redux-persist";
import storage from "redux-persist/lib/storage";

interface ClientOrganizationState {
    organizations: ClientOrganization[];
    selectedOrganization: ClientOrganization | null;
    loading: boolean;
    error: string | null;
}

const initialState: ClientOrganizationState = {
    organizations: [],
    selectedOrganization: null,
    loading: false,
    error: null,
};

// Async thunk to fetch client organizations
export const fetchClientOrganizations = createAsyncThunk(
    "clientOrganization/fetchOrganizations",
    async (_, { rejectWithValue }) => {
        try {
            const response = await getClientOrganizationsService();
            return response.data.data;
        } catch (error: any) {
            const message =
                error.response?.data?.message || "Failed to fetch organizations";
            return rejectWithValue(message);
        }
    }
);

const clientOrganizationSlice = createSlice({
    name: "clientOrganization",
    initialState,
    reducers: {
        setSelectedOrganization: (
            state,
            action: PayloadAction<ClientOrganization | null>
        ) => {
            state.selectedOrganization = action.payload;
        },
        resetClientOrganizations: (state) => {
            state.organizations = [];
            state.selectedOrganization = null;
            state.loading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchClientOrganizations.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchClientOrganizations.fulfilled, (state, action) => {
                state.loading = false;
                state.organizations = action.payload;
                // Auto-select the first organization if none is selected
                if (!state.selectedOrganization && action.payload.length > 0) {
                    state.selectedOrganization = action.payload[0];
                }
            })
            .addCase(fetchClientOrganizations.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { setSelectedOrganization, resetClientOrganizations } =
    clientOrganizationSlice.actions;

// Selectors
export const selectClientOrganizations = (state: RootState) =>
    state.clientOrganization.organizations;
export const selectSelectedOrganization = (state: RootState) =>
    state.clientOrganization.selectedOrganization;
export const selectClientOrganizationLoading = (state: RootState) =>
    state.clientOrganization.loading;
export const selectClientOrganizationError = (state: RootState) =>
    state.clientOrganization.error;

// Persist config for selected organization
const persistConfig = {
    key: "clientOrganization",
    storage,
    whitelist: ["selectedOrganization"],
};

export default persistReducer(persistConfig, clientOrganizationSlice.reducer);
